import constants from 'common/helpers/constants'
import getSubscriptionStatuses from 'common/helpers/getters/getSubscriptionStatuses'


interface ITokensInput {
  name: string
  value: string
}

interface ITokensOutput {
  [token: string]: string
}

const modifyTokens = (_tokens: ITokensInput[] = []): ITokensOutput => {
  const tokens = {}

  _tokens.forEach(({ name, value }) => {
    let token = name.replace(/^sc|Token$/g, '')

    if (token === 'FacebookSigninTokenManualEmail') {
      token = 'facebookSigninManualEmail'
    }
    if (token === 'RedeemPromoCoupon') {
      token = 'redeemCoupon'
    }
    else {
      token = token.charAt(0).toLowerCase() + token.slice(1)
    }

    tokens[token] = value
  })

  return tokens
}

interface IAbTestsInput {
  name: string
  value: string
  mixpanelProp: string
}

interface IAbTestsOutput {
  [name: string]: {
    value: string
    mixpanelProp: string
  }
}

const modifyAbTests = (_abTests: IAbTestsInput[] = []): IAbTestsOutput => {
  const abTests = {}

  _abTests.forEach(({ name, ...props }) => abTests[name] = props)

  return abTests
}

interface IFeaturesInput {
  name: string
  enabled: boolean
}

interface IFeaturesOutput {
  [name: string]: boolean
}

const modifyFeatures = (_features: IFeaturesInput[]): IFeaturesOutput => {
  const features = {}

  _features.forEach(({ name, enabled }) => features[name] = enabled)

  return features
}

interface ISubscriptionInput {
  id?: number
  plan?: object
  inCart?: boolean
  frequencyChangeable?: boolean
  upgradable?: boolean
  skippable?: boolean
  cancelable?: boolean
  status?: {
    value?: string
    subscribed?: boolean
    statusText?: string
  }
  addressStatus?: string
  [key: string]: any
}

interface IPlan {
  name?: string
  shippingPeriod?: number
  billingPeriod?: number
  description?: string
  productPrice?: number
  productsPerPeriod?: number
}

interface ISubscriptionOutput {
  currPlan: IPlan
  id: number
  isSubscribed: boolean
  isUpgradable: boolean
  isSkippable: boolean
  isCancelable: boolean
  isFrequencyChangeable: boolean
  status: string
  statusText: string
  isMonthlyPlan: boolean
  isUpgradableForMonthlyPlan: boolean
  [key: string]: any
}

const modifySubscription = ({
  id,
  plan,
  inCart, // eslint-disable-line
  frequencyChangeable,
  upgradable,
  skippable,
  cancelable,
  status: { value, subscribed, statusText }, // by graphql
  subscriptionStatus, // by rest/subscription
  addressStatus,
  ...rest
}: ISubscriptionInput = {}): ISubscriptionOutput => {
  const currPlan: IPlan = plan || {}
  const { name: currentPlanName } = currPlan
  const status = value || subscriptionStatus
  const isMonthlyPlan: boolean = currentPlanName === constants.planNames.monthly[1]
  const isUpgradableForMonthlyPlan: boolean = upgradable && isMonthlyPlan
  const subscriptionStatuses = getSubscriptionStatuses({ status, addressStatus })

  return {
    id,
    status,
    statusText,
    currPlan,
    isMonthlyPlan,
    isUpgradableForMonthlyPlan,
    isSubscribed: Boolean(subscribed || rest.subscribed),
    isUpgradable: upgradable,
    isSkippable: skippable,
    isCancelable: cancelable,
    isFrequencyChangeable: frequencyChangeable,
    ...subscriptionStatuses,
    ...rest,
  }
}

interface IClosedPopupsInput {
  name: string
  count: number
  lastUpdate: string
}

interface IClosedPopupsOutput {
  [name: string]: {
    count: number
    lastUpdate: string
  }
}

const modifyClosedPopups = (_closedPopups: IClosedPopupsInput[] = []): IClosedPopupsOutput => {
  const closedPopups = {}

  _closedPopups.forEach(({ name, count, lastUpdate }) => {
    if (!count) {
      return
    }

    closedPopups[name] = {
      count,
      lastUpdate,
    }
  })

  return closedPopups
}

interface IModifyCartInput {
  items: {
    subscription?: { [key: string]: any }
    products?: {
      id: number
      amount: number
      items: {
        id: number
        amount: number
        [key: string]: any
      }[]
    }[]
    beautyBag?: {
      items: {
        id: number
        amount: number
        [key: string]: any
      }[]
      stepsLeft: number
    }
  }
  discount: string
  shipping: { [key: string]: any }
  price: number
  appliedPriceRules: { [key: string]: any }
  recommendationTag: string
  flashSaleRemoved: boolean
}

interface IModifyCartOutput {
  items: {
    subscription: { [key: string]: any }
    products: {
      ids: number[]
      items: {
        [id: string]: {
          id: number
          amount: number
          [key: string]: any
        }
      }[]
    }
    beautyBag: {
      items: {
        id: number
        amount: number
        [key: string]: any
      }[]
      stepsLeft: number
    }
  }
  discount: string
  shipping: { [key: string]: any }
  isSubscriptionExist: boolean
  areProductsExist: boolean
  isBeautyBagExist: boolean
  isFlashSaleRemoved: boolean
  isFlashSaleExist: boolean
  totalCount: number
  totalPrice: number
  appliedPriceRules: { [key: string]: any }
  recommendationTag: string
}

const modifyCart = ({
  items: { subscription, products: _products, beautyBag: _beautyBag } = {},
  discount, shipping, price, appliedPriceRules, recommendationTag, flashSaleRemoved,
}: IModifyCartInput): IModifyCartOutput => {
  const _isBeautyBagExist = Boolean(_beautyBag && Object.keys(_beautyBag).length)
  const beautyBag = _isBeautyBagExist ? _beautyBag : {
    items: [],
    stepsLeft: 0,
  }

  let products: any = _products

  // gql retruns array instead of object
  if (_products instanceof Array) {
    products = _products.reduce((result, item) => ({
      ...result,
      [item.id]: item,
    }), {})
  }

  type NormalizedProducts = {
    ids: number[]
    items: {
      id: number
      amount: number
      [key: string]: any
    }[]
  }

  // normalize products data structure for better performance
  const normalizedProducts: NormalizedProducts = {
    ids: Object.keys(products).map((id) => Number(id)),
    items: products,
  }

  const productsData = Object.values(normalizedProducts.items)

  const productsCount = productsData
    .concat(beautyBag.items)
    .reduce((sum: number, { amount }) => sum + Number(amount), 0)

  const totalCount            = productsCount + (subscription ? 1 : 0)
  const isSubscriptionExist   = Boolean(subscription)
  const areProductsExist      = Boolean(productsCount)
  const isBeautyBagExist      = Boolean(beautyBag.items.length)
  const isFlashSaleRemoved    = Boolean(flashSaleRemoved)
  const isFlashSaleExist      = !isFlashSaleRemoved && productsData.some((item) => item.type === 'FlashSale')

  return {
    items: {
      subscription,
      products: normalizedProducts,
      beautyBag,
    },
    discount,
    shipping,
    isSubscriptionExist,
    areProductsExist,
    isBeautyBagExist,
    isFlashSaleExist,
    isFlashSaleRemoved,
    totalCount,
    totalPrice: price,
    appliedPriceRules,
    recommendationTag,
  }
}

// interface IModifyProductInput {
//   tradingItems: {
//     id: number
//     tradingId: number
//     type
//     price
//     inStock
//     volume: {
//       volume: number
//       unit: string
//     }
//     image: string
//     productGroup: string
//     color: string
//     colorName: string
//     priceWithDiscount: number
//   }[]
//   visibility: boolean
//   tagTypes: {
//     topTagId: number
//     tags: {
//       id: number
//       voteCount: number
//     }[]
//   }[]
//   sex: 'female' | 'male' | 'unisex'
//   typeGroup: ValueOf<typeof constants.productTypeGroups>
//   isLimitedEdition: boolean
//   descriptionSections: {
//     id: number
//     title: string
//     text: string
//     data: { name: string, url: string }[]
//   }[]
//   texts: { [id: string]: string }
//   heroMedia: object[]
//   heroImages: { images: object }[]
//   makeupMedia: {
//     bigImage: string
//     preview: string
//     thumb: string
//     type: string
//     key: string
//     name: string
//   }[]
//   ratings: {
//     avgRate: number
//     count: number
//     distribution: object[]
//   }
//   isProductPageGQLEnabled: boolean
// }
//
// interface IModifyProductOutput {
//   sex: 'female' | 'male' | 'unisex'
//   typeGroup: ValueOf<typeof constants.productTypeGroups>
//   isLimitedEdition: boolean
//   volumes: {
//     actualType: string
//     type: string
//     volume: string
//     volumeUnit: string
//   }[]
//   tagTypes: {
//     topTagId: number
//     tags: {
//       id: number
//       voteCount: number
//     }[]
//     topTagVoteCount?: number
//   }[]
//   isVisible: boolean
//   isGenderVisible: boolean
//   texts: { [id: string]: string }
//   heroImages: { images: object }[]
//   makeupMedia: any, // TODO: change that after isProductPageGQLEnabled deleted
//   rating: {
//     average: number
//     reviewCount: number
//     reviews: object[]
//   }
//   // makeupMedia: {
//   //   name: string
//   //   images: {
//   //     bigImage: string
//   //     preview: string
//   //     thumb: string
//   //     type: string
//   //     key: string
//   //   }
//   // }[],
// }

const modifyProduct = (props) => {
  let {
    visible, visibility,
    items, tradingItems,
    rating, ratings,
    tagTypes, sex, typeGroup, isLimitedEdition,
    descriptionSections: _descriptionSections, texts: _texts, heroMedia, heroImages,
    makeupMedia: _makeupMedia, isProductPageGQLEnabled,
    meta,
    ...rest
  } = props

  let volumes
  let isVisible

  // GQL returns meta: null ...
  if (!meta) {
    meta = {}
  }

  if (Array.isArray(tagTypes)) {
    tagTypes = tagTypes.map((tagType) => {
      const { topTagId } = tagType
      const topTag = tagType.tags.find((tag) => tag.id === topTagId)
      const topTagVoteCount = topTag ? topTag.voteCount : undefined

      return {
        ...tagType,
        topTagVoteCount,
      }
    })
  }
  else {
    tagTypes = []
  }

  // hide gender labels for Makeup and Candles
  const isGenderVisible = Boolean(
    sex
    && !isLimitedEdition
    && typeGroup !== constants.productTypeGroups.homeFragrance
    && typeGroup !== constants.productTypeGroups.makeup
    // extras
    && typeGroup !== constants.productTypeGroups.perfumeCase
    && typeGroup !== constants.productTypeGroups.candleSet
    && typeGroup !== constants.productTypeGroups.fragranceStorageBox
  )

  let texts = _texts

  if (Array.isArray(_descriptionSections)) {
    texts = _descriptionSections.reduce((accumulator: { [id: number]: string }, current: typeof _descriptionSections[0]) => {
      accumulator[current.id] = current.text || current.data

      return accumulator
    }, {})
  }

  if (Array.isArray(heroMedia)) {
    heroImages = heroMedia.map((media) => ({ images: { ...media } }))
  }

  type ProcessedMakeupMedia = {
    name: string,
    images: Omit<typeof _makeupMedia[0], 'name'>
  }

  let makeupMedia: any = _makeupMedia
  let processedMakeupMedia: ProcessedMakeupMedia

  if (isProductPageGQLEnabled) {
    processedMakeupMedia = makeupMedia.reduce((accumulator: ProcessedMakeupMedia, current: typeof _makeupMedia[0]) => {
      const { bigImage, preview, thumb, type, key, name } = current

      const isMediaExists = Boolean(accumulator[key])

      const media = {
        name,
        images: {
          bigImage,
          preview,
          thumb,
          type,
        },
      }

      accumulator[key] = isMediaExists ? [ ...accumulator[key], media ] : [ media ]

      return accumulator
    }, {})

    volumes = tradingItems.map(({ type, image, volume: { volume, unit: volumeUnit }, ...rest }) => ({
      ...rest,
      img: image,
      actualType: type,
      type: /Vial/.test(type) ? 'subscription' : 'onetime',
      volume,
      volumeUnit,
    }))

    rating = {
      average: ratings.avgRate,
      reviewCount: ratings.count,
      reviews: ratings.distribution,
    }

    isVisible = (
      visibility === constants.productVisibility.visible
      || visibility === constants.productVisibility.outOfStock
      || visibility === constants.productVisibility.landing
    )
  }
  else {
    volumes = items.map(({ type, volume, volumeUnit, ...rest }) => ({
      ...rest,
      actualType: type,
      type: /Vial/.test(type) ? 'subscription' : 'onetime',
      volume,
      volumeUnit,
    }))

    isVisible = visible
  }

  return {
    ...rest,
    rating,
    sex,
    typeGroup,
    isLimitedEdition,
    volumes,
    tagTypes,
    isVisible,
    isGenderVisible,
    texts,
    heroImages,
    makeupMedia: processedMakeupMedia || _makeupMedia,
  }
}

interface IModifyMakeupIntroProductsInput {
  name: string
  products: {
    colors: string[]
    product: {
      id: number
      img: string
      name: string
      brand: string
      retailPriceForcedVisibility: boolean
    }
  }[]
}

interface IModifyMakeupIntroProductsOutput {
  [name: string]: {
    colors: string[]
    id: number
    img: string
    name: string
    brand: string
    retailPriceForcedVisibility: boolean
  }[]
}

// TODO: add unit test
const modifyMakeupIntroProducts = (products: IModifyMakeupIntroProductsInput[]): IModifyMakeupIntroProductsOutput => {
  const newProducts = products.reduce((result, curr) => ({
    ...result,
    [curr.name]: curr.products.map((obj) => ({ ...obj.product, colors: obj.colors })),
  }), {})

  return newProducts
}

const modifyFlashSale = (data) => {
  const { id, timeLeft, tradingItem, media } = data

  return {
    ...data,
    isExpired: !timeLeft || !id,
    tradingId: tradingItem && tradingItem.id ? tradingItem.id : id,
    media: (media || []).map(({ preview }) => preview),
    isFetched: true,
  }
}


export {
  modifyCart,
  modifyTokens,
  modifyAbTests,
  modifyProduct,
  modifyFeatures,
  modifySubscription,
  modifyClosedPopups,
  modifyMakeupIntroProducts,
  modifyFlashSale,
}
